import { noop, tryOnUnmounted } from '@vueuse/core';

/*
 * Highly inspired from vueUse (useAsyncState)
 * @see https://github.com/vueuse/vueuse/blob/main/packages/core/useAsyncState/index.ts
 */

interface AsyncDataReturn<Params extends unknown[]> {
  execute: (...args: Params) => Promise<void>;
}

export interface AsyncDataOptions<D = any> {
  cancel?: () => void;
  cancelOnUnmounted?: boolean;
  immediate?: boolean;
  onError?: (error: unknown) => void;
  onSuccess?: (data: D) => Promise<void> | void;
  throwError?: boolean;
}

export const useAsyncData = <Data, Params extends unknown[]>(
  promise: ((...args: Params) => Promise<Data>) | Promise<Data>,
  options?: AsyncDataOptions<Data>,
// eslint-disable-next-line complexity
) => {
  const {
    cancel = noop,
    cancelOnUnmounted = true,
    immediate = true,
    onError = noop,
    onSuccess = noop,
    throwError,
  } = options ?? {};

  async function execute(...args: unknown[]) {
    const normalizedPromise = typeof promise === 'function'
      ? promise(...args as Params)
      : promise;

    try {
      const data = await normalizedPromise;

      await onSuccess(data);
    } catch (error) {
      onError(error);

      if (throwError) {
        throw error;
      }
    }
  }

  if (immediate) {
    void execute();
  }

  tryOnUnmounted(() => {
    if (cancelOnUnmounted) {
      cancel();
    }
  });

  return {
    execute,
  } as AsyncDataReturn<Params>;
};
